import type { IDocument } from '@cyntler/react-doc-viewer';
import React, { useCallback, useMemo } from 'react';
import type {
  AttachmentProps,
  UnsupportedAttachmentProps,
} from 'stream-chat-react';
import { Attachment } from 'stream-chat-react';

import { AttachmentChip } from './AttachmentChip';
import {
  type FileAttachment,
  getAttachmentFields,
  isFileAttachment,
  resolveFileUrl,
} from './attachmentUtils';

interface CustomAttachmentProps extends AttachmentProps {
  setPreviewingFile: (file: IDocument) => void;
}

/**
 * Custom attachment wrapper that provides custom unsupported attachment handling
 * https://github.com/GetStream/stream-chat-react/pull/1952
 */
export const CustomAttachment: React.FC<CustomAttachmentProps> = ({
  setPreviewingFile,
  ...attachmentProps
}) => {
  // Create a stable component reference
  const UnsupportedAttachmentWithPreview = useCallback(
    (unsupportedProps: UnsupportedAttachmentProps) => (
      <CustomUnsupportedAttachment
        {...unsupportedProps}
        setPreviewingFile={setPreviewingFile}
      />
    ),
    [setPreviewingFile],
  );

  return (
    <Attachment
      {...attachmentProps}
      UnsupportedAttachment={UnsupportedAttachmentWithPreview}
    />
  );
};

interface CustomUnsupportedAttachmentProps extends UnsupportedAttachmentProps {
  setPreviewingFile: (file: IDocument) => void;
}

const CustomUnsupportedAttachment: React.FC<
  CustomUnsupportedAttachmentProps
> = ({ attachment, setPreviewingFile }) => {
  if (!isFileAttachment(attachment)) {
    return null;
  }

  return (
    <FileAttachmentComponent
      attachment={attachment}
      setPreviewingFile={setPreviewingFile}
    />
  );
};

interface FileAttachmentComponentProps
  extends CustomUnsupportedAttachmentProps {
  attachment: FileAttachment; // Narrow the attachment type
}

const FileAttachmentComponent: React.FC<FileAttachmentComponentProps> = ({
  attachment,
  setPreviewingFile,
}) => {
  const fields = useMemo(() => getAttachmentFields(attachment), [attachment]);

  const handleClick = useCallback(async () => {
    const url = await resolveFileUrl({
      fileKey: fields.fileKey,
      identityId: fields.identityId,
    });

    if (url && attachment.title) {
      setPreviewingFile({
        uri: url,
        fileName: attachment.title,
      });
    }
  }, [fields, attachment.title, setPreviewingFile]);

  return (
    // Wrapper div prevents nested anchor tags since we already have an anchor tag in the parent
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      role="presentation"
    >
      <AttachmentChip
        title={attachment.title}
        mimeType={attachment.mime_type}
        onClick={() => {
          void handleClick();
        }}
        className="hover:bg-gray-100"
      />
    </div>
  );
};
